/* ─── Color utilities ─── */
const hslToRgb = (h, s, l) => {
  h /= 360;
  s /= 100;
  l /= 100;
  let r, g, b;
  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
  };
};

const rgbToHsl = (r, g, b) => {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  let h = 0,
    s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
};

const parseRgba = (rgba) => {
  const m = rgba?.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  return m ? { r: +m[1], g: +m[2], b: +m[3] } : { r: 14, g: 165, b: 233 };
};

const toRgba = (r, g, b) => `rgba(${r},${g},${b},0.85)`;

// ── Theme definitions ─────────────────────────────────────────────────────────
//
// VN Design System alignment notes:
//
// Canvas inline styles (node, link, surface) cannot use CSS custom properties,
// so they use the resolved hex/rgba values of the VN semantic tokens.
// Tailwind class strings (text, input, btn, badge, etc.) use the VN token
// names directly — these are applied to JSX elements, not canvas.
//
// Dark palette reference  → mainBg:#0E131C · surface:#121C2E · popUpbg:#1A283E
//                           surfaceBorder:#2A3C50 · inputBorder:#304658
// Light palette reference → mainBg:#d5dde8 · surface:#e4eaf2 · popUpbg:#edf1f7
//                           surfaceBorder:#a0afc2 · inputBorder:#788698
//
// Node type colors are intentionally untouched — they are semantic graph
// entity colors, not UI chrome, and have no VN token equivalent.

const THEMES = {
  dark: {
    node: {
      // surface dark (#121C2E) at 92% opacity — lifted enough for glow visibility
      circleBg: "rgba(18,28,46,0.92)",
      // velBlue at 20% — same accent used for all borders in dark mode
      circleBorder: "rgba(14,165,233,0.20)",
      circleShadow: "rgba(28,157,234,0.12)",
      circleHoverBorder: "rgba(14,165,233,0.50)",
      // mainText dark — near-white with slight warmth removed
      text: "rgba(225,232,240,0.90)",
      // mutedText dark — readable secondary labels
      typeText: "rgba(148,163,184,0.72)",
      iconColor: "rgba(28,157,234,0.75)",
      plusBg: "rgba(14,165,233,0.30)",
      plusStroke: "rgba(14,165,233,0.65)",
      plusSign: "rgba(225,232,240,0.90)",
      glowOuter: "rgba(14,165,233,0.12)",
      glowInner: "rgba(14,165,233,0.25)",
      // velBlue at 80% — active/selected border accent
      activeBorder: "rgba(14,165,233,0.80)",
      selectionRing: "rgba(14,165,233,0.55)",
      typeColors: {
        // People & roles
        PERSON: "rgba(59,130,246,0.85)",
        EMPLOYEE: "rgba(59,130,246,0.85)",
        OWNER: "rgba(96,165,250,0.85)",
        NAME: "rgba(59,130,246,0.70)",
        TITLE: "rgba(129,140,248,0.85)",

        // Organizations
        COMPANY: "rgba(168,85,247,0.85)",
        INDUSTRY: "rgba(245,158,11,0.85)",
        OCCUPATION: "rgba(34,197,94,0.80)",

        // Locations
        LOCATION: "rgba(239,68,68,0.80)",
        CITY: "rgba(248,113,113,0.80)",
        STATE: "rgba(252,165,165,0.75)",
        COUNTRY: "rgba(220,38,38,0.80)",
        POSTCODE: "rgba(251,146,60,0.80)",

        // Web & network
        WEBSITE: "rgba(6,182,212,0.85)",
        URL: "rgba(6,182,212,0.85)",
        REFERENCE_LINK: "rgba(34,211,238,0.80)",
        DOMAIN_SCANNED: "rgba(14,165,233,0.85)",
        IP_ADDRESS: "rgba(56,189,248,0.85)",
        ASN: "rgba(2,132,199,0.85)",
        WEBHOST: "rgba(8,145,178,0.85)",
        SERVER: "rgba(100,116,139,0.80)",
        SERVER_SERVICE: "rgba(148,163,184,0.80)",
        SOCIAL_MEDIA: "rgba(236,72,153,0.85)",
        JARM: "rgba(139,92,246,0.80)",

        // Security & threats
        CVE: "rgba(239,68,68,0.85)",
        SEVERITY: "rgba(245,158,11,0.90)",
        BLACKLIST: "rgba(220,38,38,0.85)",
        CAMPAIGN: "rgba(244,63,94,0.85)",
        SANCTION_LOOKUP_NAME: "rgba(244,63,94,0.85)",
        SANCTION_ENTITY: "rgba(244,63,94,0.85)",

        // Certificates & crypto
        CERTIFICATE: "rgba(16,185,129,0.85)",
        DOMAIN_CERTIFICATE: "rgba(52,211,153,0.85)",
        CRYPTO: "rgba(245,158,11,0.85)",
        SERIAL_NUMBER: "rgba(148,163,184,0.80)",

        // Legal
        COURT_FILING: "rgba(168,85,247,0.80)",
        DOCKET_NUMBER: "rgba(192,132,252,0.80)",

        // Software & tech
        SOFTWARE: "rgba(99,102,241,0.85)",
        CMS: "rgba(129,140,248,0.85)",
        OS: "rgba(79,70,229,0.85)",

        // Data & info
        DOCUMENT: "rgba(234,179,8,0.80)",
        LEDGER: "rgba(217,119,6,0.85)",
        DESCRIPTION: "rgba(148,163,184,0.80)",
        INFO: "rgba(148,163,184,0.80)",
        REASON: "rgba(148,163,184,0.75)",
        STATUS: "rgba(34,197,94,0.80)",
        ACTION: "rgba(250,204,21,0.85)",

        // Time
        DATE: "rgba(249,115,22,0.80)",
        DATETIME: "rgba(249,115,22,0.80)",

        // Scoring & ranking
        RANK: "rgba(168,85,247,0.80)",
        SCORE: "rgba(250,204,21,0.85)",
        CURRENCY: "rgba(34,197,94,0.85)",

        // Misc / fallback
        SIMILAR: "rgba(148,163,184,0.80)",
        SIZE: "rgba(148,163,184,0.80)",
        SCHOOL: "rgba(16,185,129,0.85)",
        WORKED_AT: "rgba(99,102,241,0.85)",
        BILL_OF_LADING: "rgba(20,184,166,0.85)",
        BoL: "rgba(20,184,166,0.85)",
        CONTAINER: "rgba(139,92,246,0.85)",

        // Threat intel & APT
        THREAT_ACTOR: "rgba(239,68,68,0.90)",
        MITRE_ATTACK: "rgba(244,63,94,0.90)",
        OPERATION: "rgba(245,158,11,0.85)",
        COUNTER_OPERATION: "rgba(16,185,129,0.85)",
        PLAYBOOK: "rgba(99,102,241,0.85)",
        ALIAS: "rgba(139,92,246,0.85)",
        SPONSOR: "rgba(168,85,247,0.85)",
        TARGET_COUNTRY: "rgba(220,38,38,0.85)",
        SECTOR: "rgba(245,158,11,0.80)",
        MOTIVATION: "rgba(244,63,94,0.85)",
        TOOL: "rgba(100,116,139,0.85)",
        FIRST_SEEN_DATE: "rgba(249,115,22,0.80)",
        ALIENVAULT_OTX: "rgba(20,184,166,0.85)",
        INFORMATION_SOURCE: "rgba(6,182,212,0.85)",
      },
    },
    link: {
      // velBlue at 75% — opaque enough to read clearly on dark canvas
      default: "rgba(14,165,233,0.92)",
      hovered: "rgba(56,189,248,0.98)",
      // full white — matches the manual node ring (MANUAL_RING_DARK = 0xffffff)
      manual: "rgba(255,255,255,0.95)",
      particle: "#1c9dea",
    },
    surface: {
      // surface dark (#121C2E) at 80% — matches bg-surface/80
      bg: "rgba(18,28,46,0.80)",
      bgSolid: "rgba(18,28,46,0.80)",
      // popUpbg dark (#1A283E) at 92% — matches bg-popUpbg/90
      popupBg: "rgba(26,40,62,0.92)",
      // surfaceBorder dark (#2A3C50)
      border: "rgba(42,60,80,0.90)",
      // surfaceBorder dark at higher opacity for hover
      borderHover: "rgba(56,189,248,0.40)",
      // shadow-login-panel equivalent for dark
      shadow: "0 12px 50px rgba(0,0,0,0.55)",
      // shadow-login-panel-glow equivalent for toasts
      toastShadow:
        "0 12px 50px rgba(0,0,0,0.55), 0 0 10px rgba(28,157,234,0.12)",
      popupShadow: "0 12px 50px rgba(0,0,0,0.55)",
      // velBlue at 6% for selection box fill
      selectionBoxFill: "rgba(14,165,233,0.06)",
      // velBlue at 40% for selection box stroke
      selectionBoxStroke: "rgba(14,165,233,0.40)",
      activeRow: "rgba(14,165,233,0.06)",
    },
    // ── Tailwind class strings — use VN semantic token names ──────────────────
    text: {
      primary: "text-mainText",
      secondary: "text-mutedText",
      accent: "text-velBlue",
    },
    input: {
      // bg-transparent per VN input rule — surface behind provides the fill
      bg: "bg-transparent",
      border: "border-inputBorder",
      // VN focus pattern: ring-2 ring-inputFocus/60, never border-color change
      focusBorder: "focus:ring-2 ring-inputFocus/60",
      text: "text-mainText",
      placeholder: "placeholder:text-mutedText",
    },
    btn: {
      // Ghost button pattern from VN spec
      secondary:
        "text-mainText hover:text-mainText border-btnBorder hover:bg-mainButtonBg/50 transition",
      dismissIcon: "text-mutedText hover:text-mainText transition",
    },
    badge: {
      // Flat/inline card variant — bg-surface/60 border-surfaceBorder
      bg: "bg-surface/60",
      border: "border-surfaceBorder",
    },
    // bg-mainBg/60 backdrop-blur-sm per VN modal backdrop spec
    backdrop: "bg-mainBg/60 backdrop-blur-sm",
    empty: "text-mutedText",
    check: {
      // successDot / activeCase family — keep emerald, no VN token for this
      bg: "bg-activeCase/10",
      border: "border-activeCase/30",
      icon: "text-activeCase",
    },
    // velBlue at 40%
    progressBar: "bg-velBlue/40",
    dragLine: {
      from: "#0EA5E9",
      fromOpacity: "0.8",
      to: "#22D3EE",
      toOpacity: "0.5",
      dotFill: "rgba(14,165,233,0.3)",
      dotStroke: "#0EA5E9",
    },
    // surface dark resolved hex — used by <select> option background
    selectOptionBg: "#121C2E",
  },

  light: {
    node: {
      // popUpbg light (#edf1f7) at 96% — slightly elevated from surface for readability
      circleBg: "rgba(237,241,247,0.96)",
      // velBlue at 22% — same accent family as dark mode
      circleBorder: "rgba(14,165,233,0.22)",
      circleShadow: "rgba(14,165,233,0.08)",
      circleHoverBorder: "rgba(14,165,233,0.45)",
      // mainText light — dark navy-tinted text, not pure black
      text: "rgba(15,23,42,0.88)",
      // mutedText light — readable on light blue-gray backgrounds
      typeText: "rgba(100,116,139,0.80)",
      iconColor: "rgba(14,130,200,0.80)",
      plusBg: "rgba(14,165,233,0.14)",
      plusStroke: "rgba(14,165,233,0.45)",
      plusSign: "rgba(15,23,42,0.80)",
      glowOuter: "rgba(14,165,233,0.08)",
      glowInner: "rgba(14,165,233,0.18)",
      activeBorder: "rgba(14,165,233,0.70)",
      selectionRing: "rgba(14,165,233,0.50)",
      typeColors: {
        PERSON: "rgba(37,99,235,0.90)",
        EMPLOYEE: "rgba(37,99,235,0.90)",
        OWNER: "rgba(59,130,246,0.90)",
        NAME: "rgba(37,99,235,0.75)",
        TITLE: "rgba(99,102,241,0.90)",
        COMPANY: "rgba(147,51,234,0.90)",
        INDUSTRY: "rgba(217,119,6,0.90)",
        OCCUPATION: "rgba(22,163,74,0.85)",
        LOCATION: "rgba(220,38,38,0.85)",
        CITY: "rgba(239,68,68,0.85)",
        STATE: "rgba(185,28,28,0.80)",
        COUNTRY: "rgba(185,28,28,0.85)",
        POSTCODE: "rgba(234,88,12,0.85)",
        WEBSITE: "rgba(8,145,178,0.90)",
        URL: "rgba(8,145,178,0.90)",
        REFERENCE_LINK: "rgba(6,182,212,0.85)",
        DOMAIN_SCANNED: "rgba(2,132,199,0.90)",
        IP_ADDRESS: "rgba(14,165,233,0.90)",
        ASN: "rgba(3,105,161,0.90)",
        WEBHOST: "rgba(8,145,178,0.90)",
        SERVER: "rgba(71,85,105,0.85)",
        SERVER_SERVICE: "rgba(100,116,139,0.85)",
        SOCIAL_MEDIA: "rgba(219,39,119,0.90)",
        JARM: "rgba(109,40,217,0.85)",
        CVE: "rgba(220,38,38,0.90)",
        SEVERITY: "rgba(217,119,6,0.90)",
        BLACKLIST: "rgba(185,28,28,0.90)",
        CAMPAIGN: "rgba(225,29,72,0.90)",
        SANCTION_LOOKUP_NAME: "rgba(225,29,72,0.90)",
        SANCTION_ENTITY: "rgba(225,29,72,0.90)",
        CERTIFICATE: "rgba(5,150,105,0.90)",
        DOMAIN_CERTIFICATE: "rgba(16,185,129,0.90)",
        CRYPTO: "rgba(217,119,6,0.90)",
        SERIAL_NUMBER: "rgba(100,116,139,0.85)",
        COURT_FILING: "rgba(147,51,234,0.85)",
        DOCKET_NUMBER: "rgba(168,85,247,0.85)",
        SOFTWARE: "rgba(79,70,229,0.90)",
        CMS: "rgba(99,102,241,0.90)",
        OS: "rgba(67,56,202,0.90)",
        DOCUMENT: "rgba(202,138,4,0.85)",
        LEDGER: "rgba(180,83,9,0.90)",
        DESCRIPTION: "rgba(100,116,139,0.80)",
        INFO: "rgba(100,116,139,0.80)",
        REASON: "rgba(100,116,139,0.75)",
        STATUS: "rgba(22,163,74,0.85)",
        ACTION: "rgba(202,138,4,0.90)",
        DATE: "rgba(234,88,12,0.85)",
        DATETIME: "rgba(234,88,12,0.85)",
        RANK: "rgba(147,51,234,0.85)",
        SCORE: "rgba(202,138,4,0.90)",
        CURRENCY: "rgba(22,163,74,0.90)",
        SIMILAR: "rgba(100,116,139,0.80)",
        SIZE: "rgba(100,116,139,0.80)",
        SCHOOL: "rgba(5,150,105,0.90)",
        WORKED_AT: "rgba(79,70,229,0.90)",
        BILL_OF_LADING: "rgba(13,148,136,0.90)",
        BoL: "rgba(13,148,136,0.90)",
        CONTAINER: "rgba(109,40,217,0.90)",

        // Threat intel & APT
        THREAT_ACTOR: "rgba(220,38,38,0.90)",
        MITRE_ATTACK: "rgba(225,29,72,0.90)",
        OPERATION: "rgba(217,119,6,0.90)",
        COUNTER_OPERATION: "rgba(5,150,105,0.90)",
        PLAYBOOK: "rgba(79,70,229,0.90)",
        ALIAS: "rgba(109,40,217,0.90)",
        SPONSOR: "rgba(147,51,234,0.90)",
        TARGET_COUNTRY: "rgba(185,28,28,0.90)",
        SECTOR: "rgba(217,119,6,0.85)",
        MOTIVATION: "rgba(225,29,72,0.90)",
        TOOL: "rgba(71,85,105,0.85)",
        FIRST_SEEN_DATE: "rgba(234,88,12,0.85)",
        ALIENVAULT_OTX: "rgba(13,148,136,0.90)",
        INFORMATION_SOURCE: "rgba(8,145,178,0.90)",
      },
    },
    link: {
      // velBlue at 65% — readable on light blue-gray canvas
      default: "rgba(14,165,233,0.88)",
      hovered: "rgba(2,132,199,0.98)",
      // slate-500 — matches the manual node ring (MANUAL_RING_LIGHT = 0x64748b)
      manual: "rgba(100,116,139,0.85)",
      particle: "#0284c7",
    },
    surface: {
      // surface light (#e4eaf2) at 80% — matches bg-surface/80. Flat color, no
      // white gradient — VN light mode uses cool blue-gray, not near-white.
      bg: "rgba(228,234,242,0.80)",
      bgSolid: "rgba(228,234,242,0.80)",
      // popUpbg light (#edf1f7) at 90% — matches bg-popUpbg/90
      popupBg: "rgba(237,241,247,0.90)",
      // surfaceBorder light (#a0afc2)
      border: "rgba(160,175,194,0.90)",
      // surfaceBorder light at higher opacity for hover
      borderHover: "rgba(120,134,152,0.60)",
      // shadow-login-panel for light mode — reduced opacity since bg is lighter
      shadow: "0 12px 50px rgba(0,0,0,0.18)",
      toastShadow:
        "0 12px 50px rgba(0,0,0,0.18), 0 0 12px rgba(14,165,233,0.06)",
      popupShadow: "0 12px 50px rgba(0,0,0,0.18)",
      // velBlue at 5% for selection box fill
      selectionBoxFill: "rgba(14,165,233,0.05)",
      // velBlue at 35% for selection box stroke
      selectionBoxStroke: "rgba(14,165,233,0.35)",
      activeRow: "rgba(14,165,233,0.05)",
    },
    // ── Tailwind class strings — use VN semantic token names ──────────────────
    text: {
      primary: "text-mainText",
      secondary: "text-mutedText",
      accent: "text-velBlue",
    },
    input: {
      // bg-transparent per VN input rule — surface behind provides the fill
      bg: "bg-transparent",
      border: "border-inputBorder",
      // VN focus pattern: ring-2 ring-inputFocus/60, never border-color change
      focusBorder: "focus:ring-2 ring-inputFocus/60",
      text: "text-mainText",
      placeholder: "placeholder:text-mutedText",
    },
    btn: {
      // Ghost button pattern from VN spec
      secondary:
        "text-mainText hover:text-mainText border-btnBorder hover:bg-mainButtonBg/50 transition",
      dismissIcon: "text-mutedText hover:text-mainText transition",
    },
    badge: {
      // Flat/inline card variant — bg-surface/60 border-surfaceBorder
      bg: "bg-surface/60",
      border: "border-surfaceBorder",
    },
    // bg-mainBg/60 backdrop-blur-sm per VN modal backdrop spec
    backdrop: "bg-mainBg/60 backdrop-blur-sm",
    empty: "text-mutedText",
    check: {
      // successDot / activeCase family
      bg: "bg-activeCase/10",
      border: "border-activeCase/30",
      icon: "text-activeCase",
    },
    // velBlue at 50% — slightly more visible on light canvas
    progressBar: "bg-velBlue/50",
    dragLine: {
      from: "#0284c7",
      fromOpacity: "0.7",
      to: "#06b6d4",
      toOpacity: "0.4",
      dotFill: "rgba(14,165,233,0.15)",
      dotStroke: "#0284c7",
    },
    // surface light resolved hex — used by <select> option background
    selectOptionBg: "#e4eaf2",
  },
};

export { THEMES, hslToRgb, rgbToHsl, parseRgba, toRgba };
const _cyan = "14,165,233";
const _cyanLit = "56,189,248";

export const PANEL_KEYFRAMES = `
  @keyframes arwes-panel-pulse {
    0%, 100% {
      box-shadow:
        0 0 0 1px rgba(${_cyan},0.08),
        0 0 14px 2px rgba(${_cyan},0.10),
        0 0 36px 4px rgba(${_cyan},0.04);
    }
    50% {
      box-shadow:
        0 0 0 1px rgba(${_cyan},0.16),
        0 0 20px 4px rgba(${_cyan},0.17),
        0 0 50px 7px rgba(${_cyan},0.07);
    }
  }
`;

export const panelTokens = (dark) => ({
  border: dark ? `rgba(${_cyan},0.38)` : `rgba(100,116,139,0.28)`,
  innerGlow: dark
    ? `radial-gradient(ellipse 80% 55% at 50% 0%, rgba(${_cyan},0.07) 0%, transparent 100%)`
    : "none",
  cyanText: dark ? `rgb(${_cyanLit})` : `rgb(${_cyan})`,
  cyanLitRgb: _cyanLit,
  cyanRgb: _cyan,
  mutedText: dark ? `rgba(148,163,184,0.65)` : `rgba(71,85,105,0.80)`,
  hoverBg: dark ? `rgba(${_cyan},0.04)` : `rgba(0,0,0,0.03)`,
  activeBg: dark ? `rgba(${_cyan},0.10)` : `rgba(${_cyan},0.06)`,
  divider: dark ? `rgba(${_cyan},0.12)` : `rgba(100,116,139,0.15)`,
});
